import React, { useState, useEffect, useCallback, useRef } from "react";
import { FlugSuche } from "@/components/FlugSuche";
import { TravelAssistant } from "@/components/TravelAssistant";
import { CountryExplorer, CurrencyConverter, Reisemappe, TravelReminders } from "@/components/TravelFeatures";
import { FahrzeugStudio } from "@/components/FahrzeugStudio";
import { LiteApiAccommodationSearch } from "@/components/LiteApiAccommodationSearch";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FIRMA } from "@/lib/branding";
import {
  Plane, Plus, Trash2, Camera, CheckCircle2, XCircle, Send,
  Receipt, Settings2, ArrowLeft, FileText, Loader2, Eye, Car, TrainFront, Scale, Sparkles, Download, BarChart3,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n, c = "EUR") =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: c || "EUR" }).format(n || 0);

const STATUS = {
  entwurf: { label: "Entwurf", cls: "bg-[var(--brand-border-light)] text-[var(--brand-muted)]" },
  eingereicht: { label: "Zur Freigabe", cls: "bg-amber-100 text-amber-800" },
  genehmigt: { label: "Genehmigt", cls: "bg-emerald-100 text-emerald-800" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-100 text-red-700" },
  abgerechnet: { label: "Abgerechnet", cls: "bg-[var(--brand-ink)] text-white" },
};

const PRESETS = {
  DE: { km_satz: 0.3, satz_volltag: 28, satz_halbtag: 14, currency: "EUR" },
  AT: { km_satz: 0.42, satz_volltag: 26.4, satz_halbtag: 13.2, currency: "EUR" },
  CH: { km_satz: 0.7, satz_volltag: 32, satz_halbtag: 16, currency: "CHF" },
};

const EMPTY_TRIP = {
  titel: "", ziel: "", zweck: "", kunde_id: "", kunde_name: "",
  start_datum: "", end_datum: "", km: 0, km_satz: 0.3, fahrtart: "hin_und_rueck",
  km_source: "", km_einfach: 0, km_route_start: "", km_route_ziel: "", km_route_fahrtart: "", km_route_berechnet_at: "",
  volltage: 0, halbtage: 0, satz_volltag: 28, satz_halbtag: 14,
  currency: "EUR", extra_kosten: [], notizen: "",
};

const tripTotals = (t) => {
  const fahrt = (parseFloat(t.km) || 0) * (parseFloat(t.km_satz) || 0);
  const verpf = (parseInt(t.volltage) || 0) * (parseFloat(t.satz_volltag) || 0)
    + (parseInt(t.halbtage) || 0) * (parseFloat(t.satz_halbtag) || 0);
  const extras = (t.extra_kosten || []).reduce((s, e) => s + (parseFloat(e.betrag) || 0), 0);
  return { fahrt, verpf, extras, gesamt: fahrt + verpf + extras };
};

const fmtDur = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = Math.round((min || 0) % 60);
  return h > 0 ? `${h} Std. ${m} Min.` : `${m} Min.`;
};

const OPT_ICONS = { auto: Car, bahn: TrainFront, flug: Plane };

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.entwurf;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

export const ReiseStudioTab = ({ adminUser, openQuoteById, hasFeature = () => true }) => {
  const isAdmin = adminUser?.role !== "mitarbeiter";
  const [trips, setTrips] = useState([]);
  const [settings, setSettings] = useState({ ...PRESETS.DE });
  const [customers, setCustomers] = useState([]);
  const [trip, setTrip] = useState(null);
  const [belege, setBelege] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const fileRef = useRef(null);
  const [vglStart, setVglStart] = useState("");
  const [vgl, setVgl] = useState(null);
  const [vglBusy, setVglBusy] = useState(false);
  const [analysing, setAnalysing] = useState("");
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const [expVon, setExpVon] = useState("");
  const [expBis, setExpBis] = useState("");
  const [providers, setProviders] = useState([]);

  const loadTrips = useCallback(async () => {
    try { setTrips((await axios.get(`${API}/reise/trips`)).data || []); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadTrips();
    axios.get(`${API}/reise/settings`).then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
    axios.get(`${API}/reise/providers`).then((r) => setProviders(r.data || [])).catch(() => {});
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data || [])).catch(() => {});
  }, [loadTrips]);

  const loadBelege = useCallback(async (id) => {
    try { setBelege((await axios.get(`${API}/reise/trips/${id}/belege`)).data || []); } catch { setBelege([]); }
  }, []);

  // Automatische Kilometerberechnung: nur für Routenwerte, nie über einen manuellen Eintrag.
  useEffect(() => {
    if (!trip || !trip.ziel || trip.ziel.trim().length < 3) return;
    const start = vglStart.trim() || settings.default_start || "";
    const ziel = trip.ziel.trim();
    if (!start || start.length < 3) return;
    if (trip.km_source === "manual") return;
    // Bestehende Altwerte ohne Herkunft werden bewusst nicht überschrieben.
    if (parseFloat(trip.km) > 0 && !trip.km_source) return;
    // Eine unveränderte, bereits geprüfte Route nicht wiederholt berechnen.
    if (trip.km_source === "route" && trip.km_route_start === start && trip.km_route_ziel === ziel && trip.km_route_fahrtart === trip.fahrtart) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/reise/km`, { params: { start, ziel } });
        const kmEinfach = Number(res.data?.km);
        // Der Schutz verhindert, dass eine falsche Geocodierung als extrem lange Strecke übernommen wird.
        if (!Number.isFinite(kmEinfach) || kmEinfach <= 0 || kmEinfach > 5000) {
          toast.error("Die berechnete Strecke ist unplausibel und wurde nicht übernommen. Bitte Start und Ziel prüfen oder Kilometer manuell eingeben.");
          return;
        }
        const factor = trip.fahrtart === "einfach" ? 1 : 2;
        const kmGesamt = Math.round(kmEinfach * factor * 10) / 10;
        setTrip((t) => ({
          ...t, km: kmGesamt, km_source: "route", km_einfach: kmEinfach,
          km_route_start: start, km_route_ziel: ziel,
          km_route_fahrtart: t.fahrtart, km_route_berechnet_at: new Date().toISOString(),
        }));
        toast.success(`Strecke berechnet: ${kmEinfach} km einfach (${kmGesamt} km ${factor === 1 ? "gesamt" : "Hin & Rück"})`);
      } catch (e) { /* Routendienstfehler werden nur bei manueller Aktualisierung sichtbar. */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [trip?.ziel, trip?.fahrtart, trip?.km_source, trip?.km_route_start, trip?.km_route_ziel, trip?.km_route_fahrtart, vglStart, settings.default_start]);

  const openTrip = (t) => { setTrip({ ...EMPTY_TRIP, ...t }); if (t.id) loadBelege(t.id); else setBelege([]); };
  const newTrip = () => openTrip({ ...EMPTY_TRIP, km_satz: settings.km_satz, satz_volltag: settings.satz_volltag, satz_halbtag: settings.satz_halbtag, currency: settings.currency });
  const up = (k, v) => setTrip((t) => ({ ...t, [k]: v }));
  const updateManualKm = (value) => setTrip((t) => ({ ...t, km: value, km_source: "manual", km_einfach: 0, km_route_start: "", km_route_ziel: "", km_route_fahrtart: "", km_route_berechnet_at: "" }));
  const resetKmForRoute = () => setTrip((t) => ({ ...t, km: 0, km_source: "recalculate", km_einfach: 0, km_route_start: "", km_route_ziel: "", km_route_fahrtart: "", km_route_berechnet_at: "" }));

  const runVergleich = async () => {
    if (!vglStart.trim() || !(trip.ziel || "").trim()) { toast.error("Bitte Start und Reiseziel angeben"); return; }
    setVglBusy(true);
    setVgl(null);
    try {
      const res = await axios.post(`${API}/reise/vergleich`, {
        start: vglStart, ziel: trip.ziel,
        km_satz: parseFloat(trip.km_satz) || 0.3, currency: trip.currency,
        fahrtart: trip.fahrtart || "hin_und_rueck",
      });
      setVgl(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Vergleich fehlgeschlagen"); }
    finally { setVglBusy(false); }
  };

  const applyOption = (o) => {
    const fahrtartLabel = o.fahrtart === "einfach" ? "einfache Fahrt" : "Hin- und Rückfahrt";
    if (o.typ === "auto") {
      setTrip((t) => ({
        ...t, km: o.km, fahrtart: o.fahrtart || t.fahrtart, km_source: "route", km_einfach: o.km_einfach || 0,
        km_route_start: vglStart.trim(), km_route_ziel: trip.ziel.trim(), km_route_fahrtart: o.fahrtart || t.fahrtart,
        km_route_berechnet_at: new Date().toISOString(),
      }));
      toast.success(`PKW übernommen: ${o.km} km (${fahrtartLabel})`);
    } else {
      const betrag = Math.round((parseFloat(o.kosten) || 0) * 100) / 100;
      up("extra_kosten", [...(trip.extra_kosten || []),
        { id: crypto.randomUUID(), beschreibung: `${o.label}ticket (${fahrtartLabel}): ${vgl.start.split(",")[0]} – ${vgl.ziel.split(",")[0]}`, betrag }]);
      toast.success(`${o.label} übernommen: ${fmt(betrag, trip.currency)} (${fahrtartLabel})`);
    }
  };

  const editable = trip && (!trip.status || trip.status === "entwurf" || trip.status === "abgelehnt" || (isAdmin && trip.status !== "abgerechnet"));

  const saveTrip = async (silent = false) => {
    if (!trip.titel && !trip.ziel) { toast.error("Bitte Titel oder Reiseziel angeben"); return null; }
    setBusy(true);
    try {
      const res = trip.id
        ? await axios.put(`${API}/reise/trips/${trip.id}`, trip)
        : await axios.post(`${API}/reise/trips`, trip);
      setTrip((t) => ({ ...t, ...res.data }));
      await loadTrips();
      if (!silent) toast.success("Reise gespeichert");
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
      return null;
    } finally { setBusy(false); }
  };

  const submitTrip = async () => {
    const saved = await saveTrip(true);
    if (!saved) return;
    try {
      await axios.post(`${API}/reise/trips/${saved.id}/submit`);
      toast.success("Reise zur Freigabe eingereicht");
      setTrip((t) => ({ ...t, status: "eingereicht" }));
      loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Einreichen fehlgeschlagen"); }
  };

  const decide = async (genehmigt) => {
    const kommentar = genehmigt ? "" : (window.prompt("Grund der Ablehnung (optional):") || "");
    try {
      await axios.post(`${API}/reise/trips/${trip.id}/decision`, { genehmigt, kommentar });
      toast.success(genehmigt ? "Reise genehmigt" : "Reise abgelehnt");
      setTrip((t) => ({ ...t, status: genehmigt ? "genehmigt" : "abgelehnt", kommentar }));
      loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };

  const toInvoice = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reise/trips/${trip.id}/to-invoice`);
      toast.success(`Rechnung ${res.data.invoice_nr} erstellt`);
      setTrip((t) => ({ ...t, status: "abgerechnet", invoice_id: res.data.invoice_id, invoice_nr: res.data.invoice_nr }));
      loadTrips();
      if (openQuoteById && res.data.invoice_id) openQuoteById(res.data.invoice_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const removeTrip = async (t) => {
    if (!window.confirm(`Reise „${t.titel || t.ziel}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/reise/trips/${t.id}`);
      toast.success("Reise gelöscht");
      if (trip?.id === t.id) setTrip(null);
      loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const uploadBeleg = async (file) => {
    if (!file) return;
    let id = trip.id;
    if (!id) { const saved = await saveTrip(true); if (!saved) return; id = saved.id; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await axios.post(`${API}/reise/trips/${id}/belege`, { filename: file.name, content_type: file.type || "image/jpeg", data_base64: b64 });
      toast.success("Beleg hochgeladen");
      loadBelege(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const viewBeleg = async (b) => {
    try {
      const res = await axios.get(`${API}/reise/belege/${b.id}`);
      const bin = atob(res.data.data_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: res.data.content_type }));
      window.open(url, "_blank");
    } catch { toast.error("Beleg konnte nicht geladen werden"); }
  };

  const deleteBeleg = async (b) => {
    try { await axios.delete(`${API}/reise/belege/${b.id}`); loadBelege(trip.id); } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const analyseBeleg = async (b) => {
    setAnalysing(b.id);
    try {
      const res = await axios.post(`${API}/reise/belege/${b.id}/analyse`);
      const d = res.data;
      const label = [d.kategorie, d.haendler, d.datum].filter(Boolean).join(" · ") || "Beleg";
      up("extra_kosten", [...(trip.extra_kosten || []), { id: crypto.randomUUID(), beschreibung: label, betrag: d.betrag }]);
      toast.success(`KI erkannt: ${label} – ${fmt(d.betrag, d.waehrung)} als Kostenposition übernommen`);
      if (d.waehrung && d.waehrung !== trip.currency) toast.info(`Achtung: Beleg-Währung ${d.waehrung} weicht von der Reise-Währung ${trip.currency} ab`);
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Auslesung fehlgeschlagen"); }
    finally { setAnalysing(""); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/reise/export.csv`, { params: { von: expVon, bis: expBis }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "reisekosten_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Steuerberater-Export heruntergeladen");
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const toggleStats = async () => {
    if (!showStats) {
      try { setStats((await axios.get(`${API}/reise/auswertung`)).data); } catch { toast.error("Auswertung fehlgeschlagen"); return; }
    }
    setShowStats((v) => !v);
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/reise/settings`, settings);
      toast.success("Spesensätze gespeichert");
      setShowSettings(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  const pending = trips.filter((t) => t.status === "eingereicht");

  /* ---------- Detail-Ansicht ---------- */
  if (trip) {
    const tot = tripTotals(trip);
    return (
      <div className="space-y-4" data-testid="reise-detail">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setTrip(null); loadTrips(); }} className="border-[var(--brand-border)]" data-testid="reise-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Alle Reisen
          </Button>
          <div className="flex items-center gap-2">
            <StatusBadge status={trip.status || "entwurf"} />
            {trip.invoice_nr && <span className="text-xs text-[var(--brand-muted)]">Rechnung {trip.invoice_nr}</span>}
          </div>
        </div>

        {trip.status === "abgelehnt" && trip.kommentar && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3" data-testid="reise-reject-note">
            Abgelehnt: {trip.kommentar}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-4 items-start">
          <Card className="p-5 border-[var(--brand-border)] space-y-4">
            <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Plane className="w-4 h-4" /> Reisedaten</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Titel</Label><Input value={trip.titel} onChange={(e) => up("titel", e.target.value)} disabled={!editable} placeholder="z. B. Messe Zürich" data-testid="reise-input-titel" /></div>
              <div><Label>Reiseziel</Label><Input value={trip.ziel} onChange={(e) => up("ziel", e.target.value)} disabled={!editable} placeholder="Ort / Adresse" data-testid="reise-input-ziel" /></div>
            </div>
            <div><Label>Zweck</Label><Input value={trip.zweck} onChange={(e) => up("zweck", e.target.value)} disabled={!editable} placeholder="z. B. Kundentermin, Messe" data-testid="reise-input-zweck" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Von</Label><Input type="date" value={trip.start_datum} onChange={(e) => up("start_datum", e.target.value)} disabled={!editable} data-testid="reise-input-von" /></div>
              <div><Label>Bis</Label><Input type="date" value={trip.end_datum} onChange={(e) => up("end_datum", e.target.value)} disabled={!editable} data-testid="reise-input-bis" /></div>
            </div>
            <div>
              <Label>Kunde (optional)</Label>
              <select
                className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm"
                value={trip.kunde_id}
                disabled={!editable}
                onChange={(e) => {
                  const c = customers.find((x) => x.id === e.target.value);
                  setTrip((t) => {
                    const next = { ...t, kunde_id: e.target.value, kunde_name: c ? (c.kunde_firma || c.kunde_name) : "" };
                    const adresse = c && [c.kunde_strasse, c.kunde_plz_ort].filter(Boolean).join(", ");
                    if (adresse && !(t.ziel || "").trim()) {
                      next.ziel = adresse;
                      toast.success("Kundenadresse aus dem CRM als Reiseziel übernommen");
                    }
                    return next;
                  });
                }}
                data-testid="reise-select-kunde"
              >
                <option value="">– Kein Kunde –</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.kunde_firma || c.kunde_name}</option>
                ))}
              </select>
            </div>
            <div><Label>Notizen</Label><Textarea value={trip.notizen} onChange={(e) => up("notizen", e.target.value)} disabled={!editable} rows={2} data-testid="reise-input-notizen" /></div>
          </Card>

          <Card className="p-5 border-[var(--brand-border)] space-y-4">
            <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Receipt className="w-4 h-4" /> Spesen-Kalkulator</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Fahrtart</Label>
                <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={trip.fahrtart || "hin_und_rueck"} onChange={(e) => setTrip((t) => ({ ...t, fahrtart: e.target.value, km_source: t.km_source === "manual" ? "manual" : "recalculate" }))} disabled={!editable} data-testid="reise-select-fahrtart">
                  <option value="hin_und_rueck">Hin- und Rückfahrt</option>
                  <option value="einfach">Einfache Fahrt</option>
                </select>
              </div>
              <div><Label>Kilometer <span className="text-xs font-normal text-[var(--brand-muted)]">{trip.km_source === "manual" ? "(manuell)" : "(Route)"}</span></Label><Input type="number" min="0" value={trip.km} onChange={(e) => updateManualKm(e.target.value)} disabled={!editable} data-testid="reise-input-km" placeholder="wird berechnet" /></div>
              <div><Label>Satz/km</Label><Input type="number" step="0.01" value={trip.km_satz} onChange={(e) => up("km_satz", e.target.value)} disabled={!editable} data-testid="reise-input-kmsatz" /></div>
              <div>
                <Label>Währung</Label>
                <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={trip.currency} onChange={(e) => up("currency", e.target.value)} disabled={!editable} data-testid="reise-select-currency">
                  <option value="EUR">EUR (€)</option>
                  <option value="CHF">CHF</option>
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--brand-muted)]">
              {trip.km_source === "route" && trip.km_einfach > 0 ? (
                <span>Route: {trip.km_route_start || "Start"} → {trip.km_route_ziel || "Ziel"} · {trip.km_einfach} km einfach · {trip.km} km {trip.fahrtart === "einfach" ? "gesamt" : "Hin- und Rückfahrt"}</span>
              ) : (
                <span>{trip.km_source === "manual" ? "Manueller Kilometerwert. Dieser wird nicht automatisch überschrieben." : "Für die automatische Berechnung zuerst einen Standard-Startort oder den Start im Vergleich eintragen."}</span>
              )}
              {editable && (settings.default_start || vglStart.trim()) && <Button type="button" size="sm" variant="outline" className="h-7 border-[var(--brand-border)]" onClick={resetKmForRoute} data-testid="reise-recalculate-km">Route neu berechnen</Button>}
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Volle Tage</Label><Input type="number" min="0" value={trip.volltage} onChange={(e) => up("volltage", e.target.value)} disabled={!editable} data-testid="reise-input-volltage" /></div>
              <div><Label>Satz/Tag</Label><Input type="number" step="0.01" value={trip.satz_volltag} onChange={(e) => up("satz_volltag", e.target.value)} disabled={!editable} /></div>
              <div><Label>An-/Abreise</Label><Input type="number" min="0" value={trip.halbtage} onChange={(e) => up("halbtage", e.target.value)} disabled={!editable} data-testid="reise-input-halbtage" /></div>
              <div><Label>Satz/Tag</Label><Input type="number" step="0.01" value={trip.satz_halbtag} onChange={(e) => up("satz_halbtag", e.target.value)} disabled={!editable} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Weitere Kosten (Hotel, Bahn, …)</Label>
                {editable && (
                  <Button size="sm" variant="outline" className="h-7 border-[var(--brand-border)]"
                    onClick={() => up("extra_kosten", [...(trip.extra_kosten || []), { id: crypto.randomUUID(), beschreibung: "", betrag: "" }])}
                    data-testid="reise-add-extra">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Kosten
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(trip.extra_kosten || []).map((e, i) => (
                  <div key={e.id || i} className="flex gap-2">
                    <Input placeholder="z. B. Hotel 2 Nächte" value={e.beschreibung} disabled={!editable}
                      onChange={(ev) => up("extra_kosten", trip.extra_kosten.map((x, j) => (j === i ? { ...x, beschreibung: ev.target.value } : x)))}
                      data-testid={`reise-extra-desc-${i}`} />
                    <Input type="number" step="0.01" placeholder="0,00" className="w-28" value={e.betrag} disabled={!editable}
                      onChange={(ev) => up("extra_kosten", trip.extra_kosten.map((x, j) => (j === i ? { ...x, betrag: ev.target.value } : x)))}
                      data-testid={`reise-extra-betrag-${i}`} />
                    {editable && (
                      <Button size="icon" variant="ghost" className="shrink-0 text-red-500"
                        onClick={() => up("extra_kosten", trip.extra_kosten.filter((_, j) => j !== i))}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {(trip.extra_kosten || []).length === 0 && (
                  <p className="text-xs text-[var(--brand-muted)] italic">Keine weiteren Kosten erfasst.</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Reiseoptionen-Vergleich */}
        {editable && (
          <Card className="p-5 border-[var(--brand-border)]" data-testid="reise-vergleich-card">
            <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2 mb-3">
              <Scale className="w-4 h-4" /> Reiseoptionen-Vergleich
              <span className="text-xs font-normal text-[var(--brand-muted)]">PKW · Bahn · Flug</span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1">
                <Label>Start (Abfahrtsort)</Label>
                <Input value={vglStart} onChange={(e) => setVglStart(e.target.value)} placeholder="z. B. Berlin" data-testid="vgl-input-start" />
              </div>
              <div className="flex-1">
                <Label>Ziel</Label>
                <Input value={trip.ziel} onChange={(e) => up("ziel", e.target.value)} placeholder="Reiseziel" data-testid="vgl-input-ziel" />
              </div>
              <Button onClick={runVergleich} disabled={vglBusy} className="bg-[var(--brand-ink)] hover:bg-black text-white" data-testid="vgl-run">
                {vglBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Scale className="w-4 h-4 mr-1.5" />}
                Vergleichen
              </Button>
            </div>

            {vgl && (
              <div className="mt-4 space-y-3" data-testid="vgl-result">
                <div className="rounded-lg px-4 py-3 text-sm font-medium text-white" style={{ background: "var(--brand-primary)" }} data-testid="vgl-empfehlung">
                  💡 {vgl.empfehlung}
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {vgl.optionen.map((o, idx) => {
                    const Icon = OPT_ICONS[o.typ] || Car;
                    const cheapest = Math.min(...vgl.optionen.map((x) => parseFloat(x.kosten) || 0));
                    const isBest = (parseFloat(o.kosten) || 0) === cheapest;
                    return (
                      <div key={o.typ} className="rounded-xl border p-4 flex flex-col gap-2"
                        style={{ borderColor: isBest ? "var(--brand-primary)" : "var(--brand-border)", borderWidth: isBest ? 2 : 1 }}
                        data-testid={`vgl-option-${o.typ}`}>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2 font-semibold text-sm"><Icon className="w-4 h-4" /> {o.label}</span>
                          {isBest && <span className="text-[10px] uppercase tracking-wide font-bold" style={{ color: "var(--brand-primary)" }}>Günstigste</span>}
                        </div>
                        {o.typ === "auto" ? (
                          <div className="text-xl font-bold">{fmt(o.kosten, vgl.currency)}</div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Input type="number" step="0.01" className="h-9 w-28 font-bold" value={o.kosten}
                              onChange={(e) => setVgl((v) => ({ ...v, optionen: v.optionen.map((x, j) => (j === idx ? { ...x, kosten: e.target.value } : x)) }))}
                              data-testid={`vgl-preis-${o.typ}`} />
                            <span className="text-sm font-semibold">{vgl.currency}</span>
                          </div>
                        )}
                        <div className="text-xs text-[var(--brand-muted)]">
                          <div>Dauer: {fmtDur(o.minuten)} ({o.fahrtart === "einfach" ? "einfache Fahrt" : "Hin- und Rückfahrt"})</div>
                          <div>{o.detail}</div>
                          {o.typ === "bahn" && <div className="text-emerald-700 mt-0.5">≈ {fmtDur(o.arbeitszeit_minuten)} produktiv arbeiten</div>}
                        </div>
                        <Button size="sm" variant="outline" className="mt-auto border-[var(--brand-border)]"
                          onClick={() => applyOption(o)} data-testid={`vgl-apply-${o.typ}`}>
                          In Spesen übernehmen
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[var(--brand-muted)]">{vgl.hinweis} Strecke: {vgl.km_einfach} km einfach · {vgl.km_gesamt} km {vgl.fahrtart === "einfach" ? "gesamt" : "Hin- und Rückfahrt"} ({vgl.start.split(",")[0]} → {vgl.ziel.split(",")[0]}). Preise für Bahn/Flug kannst du vor der Übernahme überschreiben.</p>
              </div>
            )}
          </Card>
        )}

        {/* Unterkunftssuche – LiteAPI/Nuitee Connect, ausschließlich Sandbox ohne Zahlungsdaten */}
        {editable && hasFeature("travel_api") && (
          <LiteApiAccommodationSearch
            trip={trip}
            editable={editable}
            onAddCost={(entry) => up("extra_kosten", [...(trip.extra_kosten || []), { id: crypto.randomUUID(), ...entry }])}
          />
        )}

        {/* Flugsuche (Duffel) */}
        {editable && hasFeature("travel_api") && (
          <FlugSuche tripId={trip.id} startOrt={vglStart || trip.ziel} zielOrt={trip.ziel} kmSatz={trip.km_satz} onSelectFlight={(f) => { toast.success("Flug vorgemerkt"); loadTrips(); }} />
        )}
        {/* Travel KI Assistent */}
        <TravelAssistant />
        {/* Travel Features (Währungsrechner, Länder-Checkliste, Reisemappe, Erinnerungen) */}
        <CountryExplorer />
        <CurrencyConverter />
        <Reisemappe tripId={trip.id} />
        <TravelReminders />
        {/* Fahrzeugstudio (Fahrtenbuch, Tankbuch, Wartung, Kosten) */}
        <FahrzeugStudio />
        {/* Belege */}
        <Card className="p-5 border-[var(--brand-border)]">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Camera className="w-4 h-4" /> Belege ({belege.length})</h3>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
              onChange={(e) => uploadBeleg(e.target.files?.[0])} data-testid="reise-beleg-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-[var(--brand-ink)] hover:bg-black text-white" data-testid="reise-beleg-upload">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
              Beleg fotografieren / hochladen
            </Button>
          </div>
          {belege.length === 0 ? (
            <p className="text-sm text-[var(--brand-muted)]">Noch keine Belege. Auf dem Handy öffnet der Button direkt die Kamera.</p>
          ) : (
            <div className="space-y-2">
              {belege.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--brand-border)] px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 shrink-0 text-[var(--brand-muted)]" /><span className="truncate">{b.filename}</span></span>
                  <span className="flex items-center gap-1 shrink-0">
                    {editable && hasFeature("ki_belege") && (b.content_type || "").startsWith("image/") && (
                      <Button size="sm" variant="outline" className="h-8 border-[var(--brand-border)] text-xs"
                        onClick={() => analyseBeleg(b)} disabled={analysing === b.id} data-testid={`reise-beleg-ki-${b.id}`}>
                        {analysing === b.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        KI auslesen
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => viewBeleg(b)} data-testid={`reise-beleg-view-${b.id}`}><Eye className="w-4 h-4" /></Button>
                    {editable && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => deleteBeleg(b)}><Trash2 className="w-4 h-4" /></Button>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Zusammenfassung + Aktionen */}
        <Card className="p-5 border-[var(--brand-border)]" data-testid="reise-summary">
          <h3 className="font-semibold text-[var(--brand-ink)] mb-3">Reisekosten-Zusammenfassung</h3>
          <div className="text-sm space-y-1.5 max-w-md">
            <div className="flex justify-between"><span className="text-[var(--brand-muted)]">Fahrtkosten ({trip.km || 0} km × {trip.km_satz})</span><span>{fmt(tot.fahrt, trip.currency)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--brand-muted)]">Verpflegungspauschale</span><span>{fmt(tot.verpf, trip.currency)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--brand-muted)]">Weitere Kosten</span><span>{fmt(tot.extras, trip.currency)}</span></div>
            <div className="flex justify-between items-center border-t border-[var(--brand-border)] pt-2 mt-2">
              <span className="font-semibold">Gesamt</span>
              <span className="font-bold text-lg" data-testid="reise-total">{fmt(tot.gesamt, trip.currency)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {editable && (
              <Button onClick={() => saveTrip()} disabled={busy} variant="outline" className="border-[var(--brand-border)]" data-testid="reise-save">
                Speichern
              </Button>
            )}
            {(!trip.status || trip.status === "entwurf" || trip.status === "abgelehnt") && (
              <Button onClick={submitTrip} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="reise-submit">
                <Send className="w-4 h-4 mr-1.5" /> Zur Freigabe einreichen
              </Button>
            )}
            {isAdmin && trip.status === "eingereicht" && (
              <>
                <Button onClick={() => decide(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reise-approve">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Genehmigen
                </Button>
                <Button onClick={() => decide(false)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reise-reject">
                  <XCircle className="w-4 h-4 mr-1.5" /> Ablehnen
                </Button>
              </>
            )}
            {isAdmin && trip.status === "genehmigt" && (
              <Button onClick={toInvoice} disabled={busy} className="bg-[var(--brand-ink)] hover:bg-black text-white" data-testid="reise-to-invoice">
                <FileText className="w-4 h-4 mr-1.5" /> Kosten in Rechnung übernehmen
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  /* ---------- Listen-Ansicht ---------- */
  return (
    <div className="space-y-4" data-testid="reise-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold text-[var(--brand-ink)] flex items-center gap-2"><Plane className="w-5 h-5" /> Business Travel Studio</h2>
          <p className="text-sm text-[var(--brand-muted)]">Geschäftsreisen planen, Spesen erfassen, freigeben & abrechnen.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={toggleStats} className="border-[var(--brand-border)]" data-testid="reise-stats-btn">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Auswertung
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowSettings((v) => !v)} className="border-[var(--brand-border)]" data-testid="reise-settings-btn">
              <Settings2 className="w-4 h-4 mr-1.5" /> Spesensätze
            </Button>
          )}
          <Button onClick={newTrip} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="reise-new">
            <Plus className="w-4 h-4 mr-1.5" /> Neue Reise
          </Button>
        </div>
      </div>

      {showStats && stats && (
        <Card className="p-5 border-[var(--brand-border)]" data-testid="reise-stats-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-[var(--brand-ink)]">Reisekosten-Auswertung</h3>
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Von</Label><Input type="date" className="h-8" value={expVon} onChange={(e) => setExpVon(e.target.value)} data-testid="reise-export-von" /></div>
              <div><Label className="text-xs">Bis</Label><Input type="date" className="h-8" value={expBis} onChange={(e) => setExpBis(e.target.value)} data-testid="reise-export-bis" /></div>
              <Button size="sm" variant="outline" onClick={exportCsv} className="border-[var(--brand-border)]" data-testid="reise-export-csv">
                <Download className="w-4 h-4 mr-1.5" /> Steuerberater-Export (CSV)
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4 max-w-lg">
            <div className="rounded-lg p-3" style={{ background: "var(--brand-bg)" }}>
              <div className="text-xs text-[var(--brand-muted)]">Reisen</div>
              <div className="text-xl font-bold" data-testid="stats-anzahl">{stats.anzahl_reisen}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "var(--brand-bg)" }}>
              <div className="text-xs text-[var(--brand-muted)]">Gesamtkosten</div>
              <div className="text-xl font-bold" data-testid="stats-gesamt">{fmt(stats.gesamt, settings.currency)}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "var(--brand-bg)" }}>
              <div className="text-xs text-[var(--brand-muted)]">Ø pro Reise</div>
              <div className="text-xl font-bold">{fmt(stats.durchschnitt_pro_reise, settings.currency)}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium mb-1.5">Pro Monat</div>
              {stats.pro_monat.length === 0 ? <p className="text-xs text-[var(--brand-muted)]">Keine Daten.</p> :
                stats.pro_monat.map((m) => (
                  <div key={m.monat} className="flex justify-between py-1 border-b border-[var(--brand-border-light)]">
                    <span className="text-[var(--brand-muted)]">{m.monat}</span><span>{fmt(m.summe, settings.currency)}</span>
                  </div>
                ))}
            </div>
            <div>
              <div className="font-medium mb-1.5">Pro Mitarbeiter</div>
              {stats.pro_mitarbeiter.map((m) => (
                <div key={m.mitarbeiter} className="flex justify-between py-1 border-b border-[var(--brand-border-light)]">
                  <span className="text-[var(--brand-muted)] truncate mr-2">{m.mitarbeiter} ({m.anzahl})</span><span>{fmt(m.summe, settings.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {showSettings && (
        <Card className="p-5 border-[var(--brand-border)]" data-testid="reise-settings-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-[var(--brand-ink)]">Standard-Spesensätze</h3>
            <div className="flex gap-1.5">
              {Object.keys(PRESETS).map((k) => (
                <Button key={k} size="sm" variant="outline" className="h-7 border-[var(--brand-border)]"
                  onClick={() => setSettings((s) => ({ ...s, ...PRESETS[k] }))} data-testid={`reise-preset-${k}`}>
                  {k}
                </Button>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <Label>Standard-Startort (Heimatort/Buero)</Label>
            <Input value={settings.default_start || ""} onChange={(e) => setSettings((s) => ({ ...s, default_start: e.target.value }))} placeholder="z. B. Hannover" data-testid="reise-set-start" />
            <p className="text-xs text-[var(--brand-muted)] mt-1">Wird automatisch als Abfahrtsort fuer die Kilometerberechnung verwendet</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Satz pro km</Label><Input type="number" step="0.01" value={settings.km_satz} onChange={(e) => setSettings((s) => ({ ...s, km_satz: e.target.value }))} data-testid="reise-set-km" /></div>
            <div><Label>Pauschale Volltag</Label><Input type="number" step="0.01" value={settings.satz_volltag} onChange={(e) => setSettings((s) => ({ ...s, satz_volltag: e.target.value }))} /></div>
            <div><Label>Pauschale An-/Abreise</Label><Input type="number" step="0.01" value={settings.satz_halbtag} onChange={(e) => setSettings((s) => ({ ...s, satz_halbtag: e.target.value }))} /></div>
            <div>
              <Label>Währung</Label>
              <select className="w-full h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" value={settings.currency} onChange={(e) => setSettings((s) => ({ ...s, currency: e.target.value }))}>
                <option value="EUR">EUR (€)</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
          </div>
          <Button onClick={saveSettings} className="mt-3 bg-[var(--brand-ink)] hover:bg-black text-white" data-testid="reise-settings-save">Speichern</Button>
          <div className="mt-5 border-t border-[var(--brand-border)] pt-4" data-testid="reise-providers">
            <h4 className="text-sm font-semibold text-[var(--brand-ink)] mb-2">API-Integrationen (austauschbare Module)</h4>
            <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
              {providers.map((p) => (
                <div key={p.slot} className="flex items-center justify-between rounded-lg border border-[var(--brand-border)] px-3 py-1.5">
                  <span>{p.label}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.status === "aktiv" ? "bg-emerald-100 text-emerald-800" : "bg-[var(--brand-border-light)] text-[var(--brand-muted)]"}`}>
                    {p.status === "aktiv" ? `aktiv${p.hinweis ? ` (${p.hinweis})` : ""}` : `vorbereitet: ${p.geplant.join(", ")}`}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[var(--brand-muted)] mt-2">Externe Anbieter (z. B. Google Maps, Amadeus, Deutsche Bahn, Booking) können hier später ohne Umbau angebunden werden.</p>
          </div>
        </Card>
      )}

      {isAdmin && pending.length > 0 && (
        <Card className="p-5 border-amber-300 bg-amber-50/50" data-testid="reise-pending-card">
          <h3 className="font-semibold text-amber-900 mb-2">Offene Freigaben ({pending.length})</h3>
          <div className="space-y-2">
            {pending.map((t) => (
              <button key={t.id} onClick={() => openTrip(t)} data-testid={`reise-pending-${t.id}`}
                className="w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-amber-200 px-4 py-2.5 text-sm text-left hover:border-amber-400 transition">
                <span className="min-w-0">
                  <span className="font-medium block truncate">{t.titel || t.ziel}</span>
                  <span className="text-xs text-[var(--brand-muted)]">von {t.created_by} · {t.start_datum || "–"}</span>
                </span>
                <span className="font-semibold shrink-0">{fmt(t.totals?.gesamt, t.currency)}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-[var(--brand-border)] overflow-hidden">
        {trips.length === 0 ? (
          <div className="p-10 text-center text-[var(--brand-muted)]">
            <Plane className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Noch keine Reisen. Lege die erste Geschäftsreise an.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--brand-border-light)]">
            {trips.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--brand-bg)] transition cursor-pointer"
                onClick={() => openTrip(t)} data-testid={`reise-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[var(--brand-ink)] truncate">{t.titel || t.ziel || "Ohne Titel"}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-xs text-[var(--brand-muted)] mt-0.5">
                    {[t.ziel, t.start_datum && `${t.start_datum}${t.end_datum ? ` – ${t.end_datum}` : ""}`, t.kunde_name, isAdmin && t.created_by]
                      .filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span className="font-semibold text-sm shrink-0">{fmt(t.totals?.gesamt, t.currency)}</span>
                {(t.status === "entwurf" || t.status === "abgelehnt" || isAdmin) && t.status !== "abgerechnet" && (
                  <Button size="icon" variant="ghost" className="shrink-0 text-red-500"
                    onClick={(e) => { e.stopPropagation(); removeTrip(t); }} data-testid={`reise-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
